// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Cookie } from "lucide-react";
import { initSentry } from "@/lib/sentry";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import { isProductionApp } from "@/lib/appHost";

import {
  OPEN_COOKIE_SETTINGS_EVENT,
  getEffectiveConsent,
  needsConsent,
  saveConsent,
  setStoredReplayRate,
} from "@/lib/cookieConsent";

const DEFAULT_VERSION = "1";

/**
 * Bandeau de consentement cookies (1er parti).
 *
 * Refuser est aussi accessible qu'accepter : les deux actions sont des boutons
 * de même niveau, et l'absence de choix vaut refus des cookies non essentiels.
 */
export const CookieConsentBanner = () => {
  const { data: security } = useSecuritySettings();
  // Le captcha ne tourne que sur l'hôte de production : inutile de proposer
  // la catégorie « Anti-robot » sur les previews Lovable ou en local.
  const recaptchaAvailable =
    security?.auth_security?.recaptcha_enabled === true && isProductionApp();


  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [version, setVersion] = useState(DEFAULT_VERSION);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draftDiagnostics, setDraftDiagnostics] = useState(false);
  const [draftRecaptcha, setDraftRecaptcha] = useState(false);

  // Réglages administrables : activation du bandeau, version de la politique,
  // taux d'échantillonnage du replay (mémorisé pour le prochain démarrage).
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", [
          "cookies_banner_enabled",
          "cookies_policy_version",
          "sentry_replay_sample_rate",
        ]);

      if (cancelled) return;

      for (const row of data ?? []) {
        const value = row.value as unknown;
        if (row.key === "cookies_banner_enabled") setBannerEnabled(value !== false);
        if (row.key === "cookies_policy_version" && value)
          setVersion(String(value).replace(/"/g, ""));
        if (row.key === "sentry_replay_sample_rate") setStoredReplayRate(Number(value));
      }
      setSettingsLoaded(true);
    };

    load().catch(() => setSettingsLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Affichage du bandeau : uniquement si aucun choix valide pour cette version.
  useEffect(() => {
    if (!settingsLoaded) return;
    setVisible(bannerEnabled && needsConsent(version));
  }, [settingsLoaded, bannerEnabled, version]);

  // Réouverture du panneau depuis un lien « Cookies ».
  useEffect(() => {
    const handler = () => {
      const current = getEffectiveConsent();
      setDraftDiagnostics(current.diagnostics);
      setDraftRecaptcha(current.recaptcha);
      setPanelOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
  }, []);

  const apply = useCallback(
    (diagnostics: boolean, recaptcha: boolean) => {
      saveConsent(
        { diagnostics, recaptcha: recaptchaAvailable ? recaptcha : false },
        version,
      );
      setVisible(false);
      setPanelOpen(false);
      // Le consentement accordé active la mesure immédiatement ; un refus
      // n'initialise rien (Sentry reste non chargé jusqu'au prochain accord).
      if (diagnostics) initSentry();
    },
    [recaptchaAvailable, version],
  );

  const openPanel = () => {
    const current = getEffectiveConsent();
    setDraftDiagnostics(current.diagnostics);
    setDraftRecaptcha(current.recaptcha);
    setPanelOpen(true);
  };

  return (
    <>
      {visible && (
        <div
          role="region"
          aria-label="Gestion des cookies"
          className="fixed inset-x-0 bottom-0 z-[70] border-t border-border bg-card/95 p-4 text-card-foreground shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/85"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            <div className="flex items-start gap-2">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <p className="text-sm">
                Nous utilisons des cookies nécessaires au fonctionnement de TAMA. Avec
                votre accord, nous pouvons aussi mesurer les erreurs pour améliorer
                l'application.{" "}
                <Link to="/legal/cookies" className="underline underline-offset-2">
                  En savoir plus
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => apply(true, true)}>
                Tout accepter
              </Button>
              <Button size="sm" variant="secondary" onClick={() => apply(false, false)}>
                Tout refuser
              </Button>
              <Button size="sm" variant="ghost" onClick={openPanel}>
                Personnaliser
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="z-[80] max-w-md">
          <DialogHeader>
            <DialogTitle>Préférences cookies</DialogTitle>
            <DialogDescription>
              Choisissez les catégories que vous acceptez. Vous pouvez modifier ce
              choix à tout moment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">Nécessaires</Label>
                <p className="text-xs text-muted-foreground">
                  Session, affichage et fenêtre de connexion. Toujours actifs.
                </p>
              </div>
              <Switch checked disabled aria-label="Cookies nécessaires (obligatoires)" />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="consent-diagnostics" className="text-sm font-medium">
                  Mesure et diagnostic
                </Label>
                <p className="text-xs text-muted-foreground">
                  Rapports d'erreurs, traces de performance et enregistrement de
                  session anonymisé.
                </p>
              </div>
              <Switch
                id="consent-diagnostics"
                checked={draftDiagnostics}
                onCheckedChange={setDraftDiagnostics}
              />
            </div>

            {recaptchaAvailable && (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="consent-recaptcha" className="text-sm font-medium">
                    Anti-robot
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Protection des formulaires contre les inscriptions automatisées.
                  </p>
                </div>
                <Switch
                  id="consent-recaptcha"
                  checked={draftRecaptcha}
                  onCheckedChange={setDraftRecaptcha}
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="secondary" onClick={() => apply(false, false)}>
              Tout refuser
            </Button>
            <Button onClick={() => apply(draftDiagnostics, draftRecaptcha)}>
              Enregistrer mes choix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
